import sidebarStyles from "./sidebar.module.css";
import { Category } from "../../types/types";
import React, { FC, useState } from "react";
import useMediaQuery from "../../hooks/useMediaQuery";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { deleteCookie } from "../../utils/token";
import { useAppSelector } from "../../services/hooks";
import questionnaireDoctor from "../../images/questionnaire_doctor.png";
import { AppImage } from "../appMedia";
import { mobileWidth, testUserEmail } from "../../utils/constants";
import {
  unSeenAppointmentsProgramsSelector,
  userSelector,
} from "../../services/selectors/user.selectors";
import packageJson from "../../../package.json";
import { useActions } from "../../services/hooks/useActions";
import { unSeenChatMessagesSelector } from "../../services/selectors/chat.selector";
import Tooltip from "../tooltip/tooltip";
import SwitchSitesButton from "../switchSitesButton/switchSitesButton";
import { tWithKeyAttr as t } from "../../i18nHelper";
import Modal from "../modals/modal/Modal";
import { getActiveRecording, forceCleanupAllLocks } from "../../utils/logoutLock";
import { useStayAwake } from "../../hooks/useStayAwake";
import { clearStayAwakeMode } from "../../utils/stayAwakeMode";

interface ISidebar {
  changeCategory: (
    e:
      | React.MouseEvent<HTMLLIElement, MouseEvent>
      | React.MouseEvent<HTMLAnchorElement, MouseEvent>,
    cat: Category
  ) => void;
  activeCategory: Category;
  toggleMenu: () => void;
  openMenu: boolean;
}

const TherapistSidebar: FC<ISidebar> = ({
  changeCategory,
  activeCategory,
  toggleMenu,
  openMenu,
}) => {
  const mobile = useMediaQuery(mobileWidth);
  const location = useLocation();
  const navigate = useNavigate();
  const isChat = location.pathname === "/chat";
  const user = useAppSelector(userSelector);
  const isTestUser = user?.email.toLowerCase() === testUserEmail;
  const unSeenChatMessages = useAppSelector(unSeenChatMessagesSelector);
  const unSeenAppointmentsPrograms = useAppSelector(
    unSeenAppointmentsProgramsSelector
  );
  const [tooltipTarget, setTooltipTarget] = useState<string | null>(null);
  const [showRecordingWarning, setShowRecordingWarning] = useState(false);
  const { logout } = useActions();
  const { isActive: isStayAwake, toggle: toggleStayAwake } = useStayAwake();

  return (
    <section
      className={`${sidebarStyles.sidebar} ${isChat && !mobile && sidebarStyles.sidebar_chat
        } ${mobile && openMenu ? sidebarStyles.sidebar_open : ""}`}
      onClick={toggleMenu}
    >

      <div
        className={`${sidebarStyles.sidebar__content_visible} ${openMenu && !mobile
          ? ""
          : mobile
            ? sidebarStyles.sidebar_open
            : sidebarStyles.sidebar__content_small
          }`}
      >

        <div
          onMouseEnter={() => setTooltipTarget("bar")}
          onMouseLeave={() => setTooltipTarget(null)}
          style={{ position: "relative", display: "inline-block" }}
        >

          <button
            className={sidebarStyles.sidebar__arrow}
            onClick={toggleMenu}
          />

          {tooltipTarget === "bar" && (
            <div className={sidebarStyles.tooltip}>
              <Tooltip isShown={true}>{t('SHOW_HIDE_MENU')}</Tooltip>
            </div>
          )}
        </div>
        <div className={sidebarStyles.sidebar__logo} />

        <SwitchSitesButton />

        <div className={sidebarStyles.sidebar__welcome} onClick={(e) => e.stopPropagation()}>
          <h2 className={sidebarStyles.sidebar__title} data-testid="user-name-sidebar">
            {user.firstName ? user.firstName : t("GUEST")}
          </h2>
          <AppImage
            onClick={(e) => {
              !mobile && e.stopPropagation();
              navigate("/profile", {
                replace: true,
                state: { pathname: "profile" },
              });
            }}
            className={sidebarStyles.sidebar__avatar}
            src={user.avatar ? user.avatar : questionnaireDoctor}
            alt={t('EDIT_PERSONAL_DATA_USER_AVATAR_ALT')}
          />
        </div>
        <div className={sidebarStyles.scrollable} onClick={(e) => {!mobile && e.stopPropagation()}}>
          <nav className={sidebarStyles.sidebar__pages}>
            <Link
              to={`/${Category.Main}`}
              className={`${sidebarStyles.sidebar__page} ${activeCategory === Category.Main &&
                sidebarStyles.sidebar__page_active
                }`}
              onClick={(e) => {
                changeCategory(e, Category.Main);
              }}
            >
              <p className={sidebarStyles.sidebar__pageTitle}>{t("HOME_SCREEN")}</p>
              <div
                className={`${sidebarStyles.sidebar__pageIcon} ${sidebarStyles.sidebar__pageIcon_myDay}`}
              />
            </Link>
            { <Link
              to={`/${Category.RecordMeeting}`}
              className={`${sidebarStyles.sidebar__page} ${activeCategory === Category.RecordMeeting &&
                sidebarStyles.sidebar__page_active
                }`}
              onClick={(e) => {
                changeCategory(e, Category.RecordMeeting);
              }}
            >
              <p className={sidebarStyles.sidebar__pageTitle}>{t("RECORD_MEETING")}</p>
              <div
                className={`${sidebarStyles.sidebar__pageIcon} ${sidebarStyles.sidebar__pageIcon_recordMeeting}`}
              />
            </Link> }
            <Link // change all divs to Links when content is ready
              to={`/${Category.Users}`}
              className={`${sidebarStyles.sidebar__page} ${activeCategory === Category.Users &&
                sidebarStyles.sidebar__page_active
                } `}
              onClick={(e) => {
                changeCategory(e, Category.Users);
                navigate(`/${Category.Users}`);
              }}
            >
              <p className={sidebarStyles.sidebar__pageTitle}>{t('USERS_PAGE_TITLE')}</p>
              <div
                className={`${sidebarStyles.sidebar__pageIcon} ${sidebarStyles.sidebar__pageIcon_treatment}`}
              />
            </Link>
            <li
              // to={`/${Category.Calendar}`}
              className={`${sidebarStyles.sidebar__page} ${activeCategory === Category.Calendar &&
                sidebarStyles.sidebar__page_active
                }`}
              onClick={(e) => {
                changeCategory(e, Category.Calendar);
                navigate(`/${Category.Calendar}`);
              }}
            >
              <p className={sidebarStyles.sidebar__pageTitle}>{t('CALENDAR_MY_APPOINTMENTS')}</p>
              <div
                className={`${sidebarStyles.sidebar__pageIcon} ${sidebarStyles.sidebar__pageIcon_calendar}`}
              />
            </li>

            <li
              // to={`/${Category.TreatmentPlan}`}
              className={`${sidebarStyles.sidebar__page} ${activeCategory === Category.TreatmentPlan &&
                sidebarStyles.sidebar__page_active
                }`}
              onClick={(e) => {
                changeCategory(e, Category.TreatmentPlan);
                navigate(`/${Category.TreatmentPlan}`);
              }}
            >
              <p className={sidebarStyles.sidebar__pageTitle}>{t("TREATMENT_PLANS")}</p>
              <div
                className={`${sidebarStyles.sidebar__pageIcon} ${sidebarStyles.sidebar__pageIcon_treatmentPlan}`}
              />
              {unSeenAppointmentsPrograms > 0 && (
                <span className={sidebarStyles.sidebar__unSeenItemsCount}>
                  {unSeenAppointmentsPrograms}
                </span>
              )}
            </li>

            <Link
              style={{ position: "relative" }}
              to={`/${Category.Messages}`}
              data-testid="messages-button"
              className={`${sidebarStyles.sidebar__page} 
                ${sidebarStyles.sidebar__page_messages}
                ${activeCategory === Category.Messages &&
                sidebarStyles.sidebar__page_active
                } `}
              onClick={() => {
                if (isTestUser) {
                  navigate(`/${Category.Messages}`);
                }
              }}
            >
              <p className={sidebarStyles.sidebar__pageTitle}>{t("MESSAGES")}</p>
              <div
                className={`${sidebarStyles.sidebar__pageIcon} ${sidebarStyles.sidebar__pageIcon_messages}`}
              />
              {unSeenChatMessages > 0 && (
                <span className={sidebarStyles.sidebar__unSeenItemsCount}>
                  {unSeenChatMessages}
                </span>
              )}
            </Link>
            <li
              // to={`/${Category.Profile}`}
              className={`${sidebarStyles.sidebar__page} ${activeCategory === Category.Profile &&
                sidebarStyles.sidebar__page_active
                }`}
              onClick={(e) => {
                changeCategory(e, Category.Profile);
                navigate(`/${Category.Profile}`);
              }}
            >
              <p className={sidebarStyles.sidebar__pageTitle}>{t("MY_PROFILE")}</p>
              <div
                className={`${sidebarStyles.sidebar__pageIcon} ${sidebarStyles.sidebar__pageIcon_profile}`}
              />
            </li>
            <li
              // to={`/${Category.Library}`}
              className={`${sidebarStyles.sidebar__page} ${activeCategory === Category.Library &&
                sidebarStyles.sidebar__page_active
                }`}
              onClick={(e) => {
                changeCategory(e, Category.Library);
                navigate(`/${Category.Library}`);
              }}
            >
              <p className={sidebarStyles.sidebar__pageTitle}>{t("PRIVENCER_LIBRARY")}</p>
              <div
                className={`${sidebarStyles.sidebar__pageIcon} ${sidebarStyles.sidebar__pageIcon_prevencher}`}
              />
            </li>

            <li
              // to={`/${Category.Forum}`}
              className={`${sidebarStyles.sidebar__page} ${activeCategory === Category.Forum &&
                sidebarStyles.sidebar__page_active
                } ${!isTestUser && sidebarStyles.sidebar__page_disabled}`}
              onClick={(e) => {
                if (isTestUser) {
                  changeCategory(e, Category.Forum);
                  navigate(`/${Category.Forum}`);
                }
              }}
            >
              <p className={sidebarStyles.sidebar__pageTitle}>{t("FORUM")}</p>
              <div
                className={`${sidebarStyles.sidebar__pageIcon} ${sidebarStyles.sidebar__pageIcon_forum}`}
              />
            </li>
          </nav>
          <ul className={sidebarStyles.sidebar__footer}>
            <li
              // to={`/${Category.Support}`}
              className={`${sidebarStyles.sidebar__footerItem} ${activeCategory === Category.Support &&
                sidebarStyles.sidebar__page_active
                }`}
              onClick={(e) => {
                changeCategory(e, Category.Support);
                navigate(`/${Category.Support}`);
              }}
            >
              <p className={sidebarStyles.sidebar__footerTitle}>{t('SUPPORT_TECH_TITLE')}</p>
              <div
                className={`${sidebarStyles.sidebar__footerIcon} ${sidebarStyles.sidebar__footerIcon_contacts}`}
              />
            </li>
            <li
              onClick={(e) => {
                changeCategory(e, Category.Tutorial);
                navigate(`/${Category.Tutorial}`);
              }}
            >
              <div
                className={`${sidebarStyles.sidebar__footerItem} ${activeCategory === Category.Tutorial &&
                  sidebarStyles.sidebar__page_active
                  }`}
              >
                <p className={sidebarStyles.sidebar__footerTitle}>{t("TUTORIAL_HELP_CENTER")}</p>
                <div
                  className={`${sidebarStyles.sidebar__footerIcon} ${sidebarStyles.sidebar__footerIcon_conditions}`}
                />
              </div>
            </li>
            <li
              onClick={(e) => {
                e.preventDefault();
                window.open("https://bpreven.com/privacy-policy/", "_blank");
              }}
            >
              <div className={`${sidebarStyles.sidebar__footerItem}`}>
                <p className={sidebarStyles.sidebar__footerTitle}>{t("PRIVACY_POLICY")}</p>
                <div className={`${sidebarStyles.sidebar__footerIcon} ${sidebarStyles.sidebar__footerIcon_conditions}`} />
              </div>
            </li>
            <li>
              <div
                className={sidebarStyles.sidebar__footerItem}
                onClick={(e) => {
                  e.stopPropagation();
                  // Check if a recording is active (this tab or another)
                  const activeRec = getActiveRecording();
                  if (activeRec) {
                    setShowRecordingWarning(true);
                    return;
                  }
                  // Notify other tabs to log out
                  try {
                    const ch = new BroadcastChannel('user_activity');
                    ch.postMessage({ type: 'forced_logout' });
                    ch.close();
                  } catch { /* ignore */ }
                  clearStayAwakeMode();
                  logout();
                  deleteCookie("accessToken");
                  navigate("/");
                }}
              >
                <p className={sidebarStyles.sidebar__footerTitle}>{t("LOGOUT")}</p>
                <div
                  className={`${sidebarStyles.sidebar__footerIcon} ${sidebarStyles.sidebar__footerIcon_logout}`}
                />
              </div>
            </li>
          </ul>

          {/* Stay Awake toggle */}
          <div className={sidebarStyles.stayAwakeToggle}>
            <label className={sidebarStyles.stayAwakeToggle__label}>
              <span className={sidebarStyles.stayAwakeToggle__text}>
                {t("STAY_AWAKE_TOGGLE") || "שמור מסך ער"}
              </span>
              <div
                className={`${sidebarStyles.stayAwakeToggle__track} ${isStayAwake ? sidebarStyles.stayAwakeToggle__track_active : ""}`}
                onClick={(e) => { e.stopPropagation(); toggleStayAwake(); }}
                title={isStayAwake
                  ? (t("STAY_AWAKE_TOOLTIP_OFF") || "כבה מצב שמור ער – המסך עלול לכבות ויציאה אוטומטית תחזור לפעול")
                  : (t("STAY_AWAKE_TOOLTIP_ON") || "הפעל מצב שמור ער – המסך לא יכבה ולא תתבצע יציאה אוטומטית")
                }
              >
                <div className={sidebarStyles.stayAwakeToggle__thumb} />
              </div>
            </label>
          </div>

          <p className={sidebarStyles.version}>{t("VERSION")}: {packageJson.version}</p>
        </div>
      </div>

      {/* Recording in progress warning modal */}
      {showRecordingWarning && (
        <Modal onClose={() => setShowRecordingWarning(false)}>
          <div style={{ padding: "30px", textAlign: "center", maxWidth: "400px" }}>
            <p style={{ fontSize: "1.2rem", marginBottom: "16px", fontWeight: "bold" }}>
              {t("LOGOUT_RECORDING_WARNING_TITLE")}
            </p>
            <p style={{ marginBottom: "24px" }}>
              {t("LOGOUT_RECORDING_WARNING_TEXT")}
            </p>
            <div style={{ display: "flex", gap: "12px", justifyContent: "center" }}>
              <button
                className="button hover"
                style={{ width: "160px" }}
                onClick={() => setShowRecordingWarning(false)}
              >
                {t("CANCEL") || "ביטול"}
              </button>
              <button
                className="button hover"
                style={{ width: "160px", backgroundColor: "#ef4444" }}
                onClick={() => {
                  setShowRecordingWarning(false);
                  forceCleanupAllLocks();
                  clearStayAwakeMode();
                  // Notify ALL other tabs to log out immediately (bypass lock check)
                  try {
                    const ch = new BroadcastChannel('user_activity');
                    ch.postMessage({ type: 'forced_logout' });
                    ch.close();
                  } catch { /* BroadcastChannel not supported — ignore */ }
                  logout();
                  deleteCookie("accessToken");
                  navigate("/");
                }}
              >
                {t("LOGOUT")}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </section>
  );
};

export default TherapistSidebar;
